import React, { useEffect, useState } from "react";
import { getAuth, signOut } from "firebase/auth";
import { getFirestore, collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import app, { professorAuth } from "../../firebase";

const auth = professorAuth;
const db = getFirestore(app);

export default function ProfessorSchedule() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profName, setProfName] = useState("Professor");
  const [profEmail, setProfEmail] = useState("");
  const [schedules, setSchedules] = useState([]);
  const [filterSchoolYear, setFilterSchoolYear] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [filterYearLevel, setFilterYearLevel] = useState("");
  const [filterSemester, setFilterSemester] = useState("");
  const [filterSection, setFilterSection] = useState("");

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate("/academics");
        return;
      }
      setProfEmail(user.email || "");
      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setProfName(userSnap.data().name || "Professor");
        }
      } catch (err) {
        console.error("Failed to load professor profile:", err);
      }
    });
    return unsubscribe;
  }, [navigate]);

  useEffect(() => {
    if (!profEmail) return;

    const fetchSchedules = async () => {
      try {
        const schedulesRef = collection(db, "schedules");
        // Primary: query schedules where canonical `prof` (email) was saved
        const q = query(schedulesRef, where("prof", "==", profEmail));
        const snap = await getDocs(q);
        let scheduleRows = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        // If nothing matched, broaden client-side by fetching recent schedules and
        // matching against several possible professor identifiers stored in documents
        if (scheduleRows.length === 0) {
          const allSnap = await getDocs(schedulesRef);
          const lowerEmail = profEmail.toLowerCase();
          const lowerName = String(profName || "").toLowerCase();
          scheduleRows = allSnap.docs
            .map((doc) => ({ id: doc.id, ...doc.data() }))
            .filter((sched) => {
              const profFields = [sched.prof, sched.professorEmail, sched.professor, sched.professorId]
                .filter(Boolean)
                .map(v => String(v).toLowerCase());
              // match email anywhere
              if (profFields.some(f => f.includes(lowerEmail))) return true;
              // match by name if available
              if (String(sched.professor || "").toLowerCase().includes(lowerName)) return true;
              return false;
            });
        }

        setSchedules(scheduleRows);
      } catch (err) {
        console.error("Failed to load schedule data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSchedules();
  }, [profEmail]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/academics");
  };

  const schoolYears = Array.from(new Set(schedules.map((row) => row.schoolYear).filter(Boolean))).sort();
  const departments = Array.from(new Set(schedules.map((row) => row.department).filter(Boolean))).sort();
  const yearLevels = Array.from(new Set(schedules.map((row) => row.yearLevel).filter(Boolean))).sort();
  const semesters = Array.from(new Set(schedules.map((row) => row.semester).filter(Boolean))).sort();
  const sections = Array.from(new Set(schedules.map((row) => row.section).filter(Boolean))).sort();

  const filteredSchedules = schedules.filter((row) => {
    return (
      (!filterSchoolYear || row.schoolYear === filterSchoolYear) &&
      (!filterDepartment || row.department === filterDepartment) &&
      (!filterYearLevel || row.yearLevel === filterYearLevel) &&
      (!filterSemester || row.semester === filterSemester) &&
      (!filterSection || row.section === filterSection)
    );
  });

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", flexDirection: "row" }}>
      <div style={{ width: 220, background: "#155c2b", color: "#fff", display: "flex", flexDirection: "column", alignItems: "stretch", padding: "32px 0", minHeight: "100vh", boxShadow: "2px 0 8px #eee" }}>
        <div style={{ fontWeight: 700, fontSize: 22, textAlign: "center", marginBottom: 40, letterSpacing: 2 }}>ENROLLEASE</div>
        <button onClick={() => navigate('/professor')} style={{ background: "transparent", color: "#fff", border: "none", borderRadius: 6, padding: "14px 0", fontWeight: 600, fontSize: 17, cursor: "pointer", textAlign: "left", paddingLeft: 32, marginBottom: 8, marginRight: 12, transition: "background 0.2s" }}>Home</button>
        <button onClick={() => navigate('/professor/schedule')} style={{ background: "#e67e22", color: "#fff", border: "none", borderRadius: 6, padding: "14px 0", fontWeight: 600, fontSize: 17, cursor: "pointer", textAlign: "left", paddingLeft: 32, marginBottom: 8, marginRight: 12, boxShadow: "0 2px 8px #e67e22aa" }}>Schedule</button>
        <button onClick={() => navigate('/professor/grading')} style={{ background: "transparent", color: "#fff", border: "none", borderRadius: 6, padding: "14px 0", fontWeight: 600, fontSize: 17, cursor: "pointer", textAlign: "left", paddingLeft: 32, marginBottom: 8, marginRight: 12, transition: "background 0.2s" }}>Grading</button>
        <div style={{ flex: 1 }} />
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <div style={{ height: 80, background: "#fff", display: "flex", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", padding: "0 40px", justifyContent: "space-between" }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, color: "#155c2b", fontWeight: 600 }}>Professor Schedule</h1>
            <div style={{ color: "#4a5568", marginTop: 4 }}>{profName}</div>
          </div>
          <button onClick={handleLogout} style={{ background: "#e67e22", color: "#fff", border: "none", borderRadius: 10, padding: "12px 24px", fontWeight: 700, cursor: "pointer" }}>Log out</button>
        </div>

        <div style={{ flex: 1, padding: 40, overflowY: "auto" }}>
          <div style={{ maxWidth: 1300, margin: "0 auto" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 16, marginBottom: 24 }}>
              <select value={filterSchoolYear} onChange={(e) => setFilterSchoolYear(e.target.value)} style={{ padding: 12, borderRadius: 10, border: "1px solid #cbd5e1", fontSize: 14 }}>
                <option value="">Filter by School Year</option>
                {schoolYears.map((year) => <option key={year} value={year}>{year}</option>)}
              </select>
              <select value={filterDepartment} onChange={(e) => setFilterDepartment(e.target.value)} style={{ padding: 12, borderRadius: 10, border: "1px solid #cbd5e1", fontSize: 14 }}>
                <option value="">Filter by Department</option>
                {departments.map((dep) => <option key={dep} value={dep}>{dep}</option>)}
              </select>
              <select value={filterYearLevel} onChange={(e) => setFilterYearLevel(e.target.value)} style={{ padding: 12, borderRadius: 10, border: "1px solid #cbd5e1", fontSize: 14 }}>
                <option value="">Filter by Year Level</option>
                {yearLevels.map((year) => <option key={year} value={year}>{year}</option>)}
              </select>
              <select value={filterSemester} onChange={(e) => setFilterSemester(e.target.value)} style={{ padding: 12, borderRadius: 10, border: "1px solid #cbd5e1", fontSize: 14 }}>
                <option value="">Filter by Semester</option>
                {semesters.map((sem) => <option key={sem} value={sem}>{sem}</option>)}
              </select>
              <select value={filterSection} onChange={(e) => setFilterSection(e.target.value)} style={{ padding: 12, borderRadius: 10, border: "1px solid #cbd5e1", fontSize: 14 }}>
                <option value="">Filter by Section</option>
                {sections.map((section) => <option key={section} value={section}>{section}</option>)}
              </select>
            </div>

            <div style={{ background: "#fff", borderRadius: 18, padding: 24, boxShadow: "0 10px 30px rgba(22, 44, 32, 0.05)" }}>
              {loading ? (
                <div style={{ color: "#718096" }}>Loading schedule...</div>
              ) : filteredSchedules.length === 0 ? (
                <div style={{ color: "#718096" }}>No schedule entries match your filters.</div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#f7faf7" }}>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", fontWeight: 700, color: "#334155" }}>School Year</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", fontWeight: 700, color: "#334155" }}>Department</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", fontWeight: 700, color: "#334155" }}>Year Level</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", fontWeight: 700, color: "#334155" }}>Semester</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", fontWeight: 700, color: "#334155" }}>Section</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", fontWeight: 700, color: "#334155" }}>Course</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", fontWeight: 700, color: "#334155" }}>Day</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", fontWeight: 700, color: "#334155" }}>Time</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", fontWeight: 700, color: "#334155" }}>Room</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSchedules.map((row) => (
                        <tr key={row.id}>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>{row.schoolYear || "—"}</td>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>{row.department || "—"}</td>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>{row.yearLevel || "—"}</td>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>{row.semester || "—"}</td>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>{row.section || "—"}</td>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>{row.courseId || "—"} {row.courseName ? `- ${row.courseName}` : ""}</td>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>{row.day || "—"}</td>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>{row.time || "—"}</td>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>{row.room || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
